<template>
  <div class="lg:max-w-screen-2xl mx-auto flex flex-col px-4 relative">
    <div class="my-4">
      <div class="flex max-md:flex-col gap-4">
        <PageBreadcrumb class="flex-grow" />
      </div>
    </div>

    <!-- RouterView for content -->
    <RouterView />
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from 'vue'
import { useRoute, RouterView } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'primevue/usetoast'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import { useFCCMerchantStore, useFCCAlertStore } from '@/stores/fcc'
import { useFrontegg } from '@frontegg/vue'
import { errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'

const route = useRoute()
const merchantStore = useFCCMerchantStore()
const alertStore = useFCCAlertStore()
const { merchants } = storeToRefs(merchantStore)
const { authState } = useFrontegg()
const toast = useToast()
const accessToken = computed(() => authState?.user?.accessToken || '')
const { hasPerms } = usePerms()

function showApiError(err: unknown, title: string) {
  const apiError = normalizeError(err)
  errorToastWithDetails(toast, title, apiError.toToastMessage(), apiError)
}

const currentMerchantId = computed(() => merchantStore.currentMerchantId)

// Sync merchant state from route params
function syncMerchantStateFromRoute() {
  if (route.name === 'fccMerchantDetails' && route.params.id) {
    const merchantId = Number(route.params.id)
    if (!isNaN(merchantId) && merchantId !== currentMerchantId.value) {
      // Find merchant in store to get the name
      const merchant = merchantStore.getMerchantById(merchantId)
      if (merchant) {
        merchantStore.setCurrentMerchantId(merchantId)
      } else {
        // If merchant not in store, set ID and fetch details
        merchantStore.setCurrentMerchantId(merchantId)
      }
    }
  }
}

// Watch route changes to sync merchant state
watch(
  () => route.name,
  () => {
    syncMerchantStateFromRoute()
  },
  { immediate: true },
)

// Watch route params for merchant details
watch(
  () => route.params.id,
  () => {
    syncMerchantStateFromRoute()
  },
  { immediate: true },
)

// Initialize on mount
onMounted(async () => {
  syncMerchantStateFromRoute()
})

// Trigger FCC data fetches only once a valid token is available
watch(
  () => accessToken.value,
  async token => {
    if (!token) return
    if (!hasPerms(PERMISSIONS.FCC_READ)) return

    if (merchants.value.length === 0) {
      merchantStore.fetchMerchants(authState).catch(error => {
        console.error('Failed to fetch merchants:', error)
        showApiError(error, 'Failed to load merchants')
      })
    }

    if (merchantStore.eventTypes.length === 0) {
      merchantStore.fetchEventTypes(authState).catch(error => {
        console.error('Failed to fetch event types:', error)
      })
    }

    if (merchantStore.availableActions.length === 0) {
      merchantStore.fetchAvailableActions(authState).catch(error => {
        console.error('Failed to fetch available actions:', error)
      })
    }

    if (alertStore.alerts.length === 0) {
      alertStore.fetchAlerts(authState).catch(error => {
        console.error('Failed to fetch alerts:', error)
        showApiError(error, 'Failed to load alerts')
      })
    }
  },
  { immediate: true },
)
</script>

<style scoped></style>
